import RoomSignaling, { SignalingState } from './RoomSignaling';
import RoomSession from './RoomSession'
import { RoomMembers, MemberViewState, encodeAccountAndDisplayName } from './RoomMembers'
import { AwaitQueue } from 'awaitqueue'
import { Delay } from './Delay'
import * as rx from 'rxjs'
import * as rxops from 'rxjs/operators'
import * as messages from './Messages';
import * as stat from './WebRTCStatistics'
import TimeSeries from './TimeSeries'
import CounterMetric from './CounterMetric'
import { KaldunTicketSubject, CanSendAudio, CanSendVideo } from './TicketSubject'
import { IcePolicy, OutgoingMediaStreamStatsState, OutgoingMediaViewState, RTCIceServerWithHostname, TransportStatisticsViewState, VideoLayerStat } from './Domain';
import { CompressedRTCStatsReport, EnableRTCStatsReporting } from '../generated/submodules/client-messages/protos/websocket';
import { compressData, createProtoRTCStatsReport, getDictionary } from './StatsConverter';
import { KaldunTicketsClient } from '../generated/submodules/garcon-api/TicketsServiceClientPb'
import { TicketRequest, TicketResponse, Ticket, LocationData } from '../generated/submodules/garcon-api/tickets_pb'

interface RoomConfiguration {
    roomId: string;
    accountId: string;
    clientId: string;
    displayName: string;
    garconUrl: string;
    region: string | null,
    subject: KaldunTicketSubject,
    enableTcp: boolean,
    enableScreenShare?: boolean,
    icePolicy: IcePolicy,
    maxCameraCaptureDimension: number | null
    allowAutomaticSimulcast: boolean | null
    videoProducerBandwidthLimitKbps: number | null
    enableFrequentKeyFrameRequester: boolean;
    enableFrequentKeyFrameGenerator: boolean;
    maxCameraSendBitrate: number | null;
    maxCameraSendFrameRate: number | null;
    clientRegion: string | null;
    clientRegionSubdivision: string | null;
}

export type RoomMemberViewState = MemberViewState;

export type RoomViewState = {
    members: Array<MemberViewState>
    outgoingMedia: null | OutgoingMediaViewState,
    transportStatistics: null | TransportStatisticsViewState
}

export class RoomController {
    private readonly queue = new AwaitQueue();
    private zstdDictionary : { etag: string, buffer: ArrayBuffer } | null = null;
    private readonly configuration: RoomConfiguration;
    private readonly cameraAndMicrophone: OutgoingMediaStreamStatsState = {
        stream: new MediaStream(),
        audioTrackStat: {
            audioLevel: new TimeSeries("audioLevel", 60),
            echoReturnLoss: new TimeSeries("echoReturnLoss", 60),
            bytesSent: new CounterMetric("bytesSent", 60),
            packetsSent: new CounterMetric("packetsSent", 60),
            headerBytesSent: new CounterMetric("headerBytesSent", 60),
            bytesDiscardedOnSend: new CounterMetric("bytesDiscardedOnSend", 60),
            packetsDiscardedOnSend: new CounterMetric("packetsDiscardedOnSend", 60),
            targetBitrate: new TimeSeries("targetBitrate", 60),
            retransmittedBytesSent: new CounterMetric("retransmittedBytesSent", 60),
            retransmittedPacketsSent: new CounterMetric("retransmittedPacketsSent", 60),
            nackCount: new CounterMetric("nackCount", 60),
            jitter: new TimeSeries("jitter", 60),
            packetsLost: new CounterMetric("packetsLost", 60),
            fractionLost: new TimeSeries("fractionLost", 60),
            roundTripTime: new TimeSeries("roundTripTime", 60),
            roundTripTimeMeasurements: new CounterMetric("roundTripTimeMeasurements", 60),
            totalRoundTripTime: new CounterMetric("totalRoundTripTime", 60),
        },
        videoTrackStat: {
            layers: new Map()
        }
    };

    private readonly screenAndAudio: OutgoingMediaStreamStatsState = {
        stream: new MediaStream(),
        audioTrackStat: {
            audioLevel: new TimeSeries("audioLevel", 60),
            echoReturnLoss: new TimeSeries("echoReturnLoss", 60),
            bytesSent: new CounterMetric("bytesSent", 60),
            packetsSent: new CounterMetric("packetsSent", 60),
            headerBytesSent: new CounterMetric("headerBytesSent", 60),
            bytesDiscardedOnSend: new CounterMetric("bytesDiscardedOnSend", 60),
            packetsDiscardedOnSend: new CounterMetric("packetsDiscardedOnSend", 60),
            targetBitrate: new TimeSeries("targetBitrate", 60),
            retransmittedBytesSent: new CounterMetric("retransmittedBytesSent", 60),
            retransmittedPacketsSent: new CounterMetric("retransmittedPacketsSent", 60),
            nackCount: new CounterMetric("nackCount", 60),
            jitter: new TimeSeries("jitter", 60),
            packetsLost: new CounterMetric("packetsLost", 60),
            fractionLost: new TimeSeries("fractionLost", 60),
            roundTripTime: new TimeSeries("roundTripTime", 60),
            roundTripTimeMeasurements: new CounterMetric("roundTripTimeMeasurements", 60),
            totalRoundTripTime: new CounterMetric("totalRoundTripTime", 60),
        },
        videoTrackStat: {
            layers: new Map()
        }
    };

    private readonly transportStatistics: {
        iceState?: RTCIceTransportState
        bytesSent: CounterMetric;
        bytesReceived: CounterMetric;
        packetsSent: CounterMetric;
        packetsReceived: CounterMetric;
        candidatePair?: {
            id: string;
            bytesSent: CounterMetric;
            bytesReceived: CounterMetric;
            packetsSent: CounterMetric;
            packetsReceived: CounterMetric;
            currentRoundTripTime: TimeSeries;
            availableOutgoingBitrate: TimeSeries;
            availableIncomingBitrate: TimeSeries;
            local: {
                address?: string
                port?: number
                protocol?: string
                relayProtocol?: string
                candidateType?: string
            }
            remote: {
                address?: string
                port?: number
                protocol?: string
            }
        }
    } = {
        bytesSent: new CounterMetric("transportBytesSent", 60),
        bytesReceived: new CounterMetric("transportBytesReceived", 60),
        packetsSent: new CounterMetric("transportPacketsSent", 60),
        packetsReceived: new CounterMetric("transportPacketsReceived", 60),
    }

    private readonly signaling = new RoomSignaling(Math.floor((1 + Math.random()) * 100000000));
    private readonly signalingStateChangedSub: rx.SubscriptionLike;
    private readonly maintenanceNotificationsSub: rx.SubscriptionLike;
    private readonly roomUpdatedNotificationsSub: rx.SubscriptionLike;

    private session: null | {
        obj: RoomSession,
        trackAddedSub: rx.SubscriptionLike
        iceRestartSub: rx.SubscriptionLike
        statisticsSub: rx.SubscriptionLike
        enableRtcReportingSub: rx.SubscriptionLike
    } = null;

    private viewStateChangedSubj = new rx.Subject<void>();
    private roomMembers = new RoomMembers();

    private get cameraTrack(): MediaStreamTrack | null {
        const videoTracks = this.cameraAndMicrophone.stream.getVideoTracks();
        if (videoTracks.length === 0) {
            return null;
        }
        return videoTracks[0];
    }

    private set cameraTrack(track: MediaStreamTrack | null) {
        if (track && track.kind !== "video") {
            throw new Error("Wrong track kind");
        }
        const videoTracks = this.cameraAndMicrophone.stream.getVideoTracks();
        for (const vt of videoTracks) {
            if (track !== vt) {
                vt.stop();
            }
            this.cameraAndMicrophone.stream.removeTrack(vt);
        }
        if (track !== null) {
            this.cameraAndMicrophone.stream.addTrack(track);
        }
        this.viewStateChangedSubj.next();
    }

    private get screenVideoTrack(): MediaStreamTrack | null {
        const videoTracks = this.screenAndAudio.stream.getVideoTracks();
        if (videoTracks.length === 0) {
            return null;
        }
        return videoTracks[0];
    }

    private set screenVideoTrack(track: MediaStreamTrack | null) {
        if (track && track.kind !== "video") {
            throw new Error("Wrong track kind");
        }
        const videoTracks = this.screenAndAudio.stream.getVideoTracks();
        for (const vt of videoTracks) {
            if (track !== vt) {
                vt.stop();
            }
            this.screenAndAudio.stream.removeTrack(vt);
        }
        if (track !== null) {
            this.screenAndAudio.stream.addTrack(track);
        }
        this.viewStateChangedSubj.next();
    }

    private get screenAudioTrack(): MediaStreamTrack | null {
        const audioTracks = this.screenAndAudio.stream.getAudioTracks();
        if (audioTracks.length === 0) {
            return null;
        }
        return audioTracks[0];
    }

    private set screenAudioTrack(track: MediaStreamTrack | null) {
        if (track && track.kind !== "audio") {
            throw new Error("Wrong track kind 'audio'");
        }
        const audioTracks = this.screenAndAudio.stream.getAudioTracks();
        for (const at of audioTracks) {
            if (track !== at) {
                at.stop();
            }
            this.screenAndAudio.stream.removeTrack(at);
        }
        if (track !== null) {
            this.screenAndAudio.stream.addTrack(track);
        }
        this.viewStateChangedSubj.next();
    }

    private get microphoneTrack(): MediaStreamTrack | null {
        const audioTracks = this.cameraAndMicrophone.stream.getAudioTracks();
        if (audioTracks.length === 0) {
            return null;
        }
        return audioTracks[0];
    }

    private set microphoneTrack(track: MediaStreamTrack | null) {
        if (track && track.kind !== "audio") {
            throw new Error("Wrong track kind 'audio'");
        }
        const audioTracks = this.cameraAndMicrophone.stream.getAudioTracks();
        for (const at of audioTracks) {
            if (track !== at) {
                at.stop();
            }
            this.cameraAndMicrophone.stream.removeTrack(at);
        }
        if (track !== null) {
            this.cameraAndMicrophone.stream.addTrack(track);
        }
        this.viewStateChangedSubj.next();
    }

    constructor(configuration: RoomConfiguration) {
        this.configuration = configuration;
        this.signalingStateChangedSub = this.signaling.signalingStateChanged.subscribe(s => this.onSignalingStateChanged(s));
        this.maintenanceNotificationsSub = this.signaling.maintenanceNotification.subscribe(m => this.onMaintenanceNotification(m));
        this.roomUpdatedNotificationsSub = this.signaling.roomUpdatedNotification.subscribe(u => this.onRoomUpdatedNotification(u));
    }

    private get transportStatisticsViewState(): null | TransportStatisticsViewState {
        return this.session != null ? {
            bytesReceived: {
                total: this.transportStatistics.bytesReceived.value,
                derivative: this.transportStatistics.bytesReceived.derivative,
            },
            bytesSent: {
                total: this.transportStatistics.bytesSent.value,
                derivative: this.transportStatistics.bytesSent.derivative,
            },
            packetsReceived: {
                total: this.transportStatistics.packetsReceived.value,
                derivative: this.transportStatistics.packetsReceived.derivative,
            },
            packetsSent: {
                total: this.transportStatistics.packetsSent.value,
                derivative: this.transportStatistics.packetsSent.derivative,
            },
            candidatePair: this.transportStatistics.candidatePair ? {
                bytesReceived: {
                    total: this.transportStatistics.candidatePair.bytesReceived.value,
                    derivative: this.transportStatistics.candidatePair.bytesReceived.derivative,
                },
                bytesSent: {
                    total: this.transportStatistics.candidatePair.bytesSent.value,
                    derivative: this.transportStatistics.candidatePair.bytesSent.derivative,
                },
                packetsReceived: {
                    total: this.transportStatistics.candidatePair.packetsReceived.value,
                    derivative: this.transportStatistics.candidatePair.packetsReceived.derivative,
                },
                packetsSent: {
                    total: this.transportStatistics.candidatePair.packetsSent.value,
                    derivative: this.transportStatistics.candidatePair.packetsSent.derivative,
                },
                availableIncomingBitrate: this.transportStatistics.candidatePair.availableIncomingBitrate.lastValue,
                availableOutgoingBitrate: this.transportStatistics.candidatePair.availableOutgoingBitrate.lastValue,
                currentRoundTripTime: this.transportStatistics.candidatePair.currentRoundTripTime.lastValue,
                local: {
                    address: this.transportStatistics.candidatePair.local.address,
                    port: this.transportStatistics.candidatePair.local.port,
                    protocol: this.transportStatistics.candidatePair.local.protocol,
                    relayProtocol: this.transportStatistics.candidatePair.local.relayProtocol,
                    candidateType: this.transportStatistics.candidatePair.local.candidateType,
                },
                remote: {
                    address: this.transportStatistics.candidatePair.remote.address,
                    port: this.transportStatistics.candidatePair.remote.port,
                    protocol: this.transportStatistics.candidatePair.remote.protocol,
                }
            } : undefined
        } : null;
    }

    private get outgoingMediaViewState(): null | OutgoingMediaViewState {
        if (!CanSendAudio(this.configuration.subject) && !CanSendVideo(this.configuration.subject)) {
            return null;
        }
        const micTack = this.microphoneTrack;
        const camTrack = this.cameraTrack;
        const screenVideoTrack = this.screenVideoTrack;
        const screenAudioTrack = this.screenAudioTrack;

        const createVideoTrackStats = (track: MediaStreamTrack | null, streamState: OutgoingMediaStreamStatsState) => {
            return track ? {
                captureFps: streamState.videoTrackStat.captureFps,
                captureHeight: streamState.videoTrackStat.captureHeight,
                captureWidth: streamState.videoTrackStat.captureWidth,
                layers: Array.from(streamState.videoTrackStat.layers.values()).map(layer => {
                    return ({
                        ssrc: layer.ssrc,
                        rtxSsrc: layer.rtxSsrc,
                        codec: layer.codec,
                        codecPayloadType: layer.codecPayloadType,
                        rid: layer.rid,
                        packetsSent: {
                            total: layer.packetsSent.value,
                            derivative: layer.packetsSent.derivative,
                        },
                        bytesSent: {
                            total: layer.bytesSent.value,
                            derivative: layer.bytesSent.derivative,
                        },
                        headerBytesSent: {
                            total: layer.headerBytesSent.value,
                            derivative: layer.headerBytesSent.derivative,
                        },
                        packetsDiscardedOnSend: {
                            total: layer.packetsDiscardedOnSend.value,
                            derivative: layer.packetsDiscardedOnSend.derivative,
                        },
                        bytesDiscardedOnSend: {
                            total: layer.bytesDiscardedOnSend.value,
                            derivative: layer.bytesDiscardedOnSend.derivative,
                        },
                        retransmittedPacketsSent: {
                            total: layer.retransmittedPacketsSent.value,
                            derivative: layer.retransmittedPacketsSent.derivative,
                        },
                        retransmittedBytesSent: {
                            total: layer.retransmittedBytesSent.value,
                            derivative: layer.retransmittedBytesSent.derivative,
                        },
                        targetBitrate: {
                            value: layer.targetBitrate.lastValue || -1,
                        },
                        encodedFramesPerSecond: {
                            value: layer.encodedFramesPerSecond.lastValue || -1,
                        },
                        framesSent: {
                            total: layer.framesSent.value,
                            derivative: layer.framesSent.derivative,
                        },
                        framesEncoded: {
                            total: layer.framesEncoded.value,
                            derivative: layer.framesEncoded.derivative,
                        },
                        keyFramesEncoded: {
                            total: layer.keyFramesEncoded.value,
                            derivative: layer.keyFramesEncoded.derivative,
                        },
                        framesDiscardedOnSend: {
                            total: layer.framesDiscardedOnSend.value,
                            derivative: layer.framesDiscardedOnSend.derivative,
                        },
                        hugeFramesSent: {
                            total: layer.hugeFramesSent.value,
                            derivative: layer.hugeFramesSent.derivative,
                        },
                        firCount:  {
                            total: layer.firCount.value,
                            derivative: layer.firCount.derivative,
                        },
                        pliCount:  {
                            total: layer.pliCount.value,
                            derivative: layer.pliCount.derivative,
                        },
                        nackCount:  {
                            total: layer.nackCount.value,
                            derivative: layer.nackCount.derivative,
                        },
                        width: layer.frameWidth.lastValue || 0,
                        height: layer.frameHeight.lastValue || 0,
                        qualityLimitationReason: layer.qualityLimitationReason,
                        qualityLimitationDuration: {
                            none: layer.qualityLimitationDuration.none.value / 1000,
                            cpu: layer.qualityLimitationDuration.cpu.value / 1000,
                            bandwidth: layer.qualityLimitationDuration.bandwidth.value / 1000,
                            other: layer.qualityLimitationDuration.other.value / 1000,
                        },
                        encodeTime: {
                            total: layer.totalEncodeTime.value,
                            msPerFrame: layer.framesEncoded.derivative / layer.totalEncodeTime.increase
                        },
                        packetSendDelay: {
                            total: layer.totalPacketSendDelay.value,
                            derivative: layer.totalPacketSendDelay.derivative
                        },
                        jitter: layer.jitter.isEmpty ? null : {
                            value: (layer.jitter.lastValue || 0),
                        },
                        rtt: (layer.roundTripTime.isEmpty || layer.roundTripTimeMeasurements.value === 0) ? null : {
                            current: (layer.roundTripTime.lastValue || 0),
                            average: layer.totalRoundTripTime.value / layer.roundTripTimeMeasurements.value,
                        },
                        estimatedPacketsLoss: (layer.packetsLost.isEmpty || layer.fractionLost.isEmpty) ? null : {
                            fractionLost: layer.fractionLost.lastValue || 0,
                            packetsLost: layer.packetsLost.value || 0,
                        }
                    })
                }).sort((x, y) => x.width * x.height - y.width * y.height)
            } : undefined;
        }

        const createAudioTrackStats = (track: MediaStreamTrack | null, streamState: OutgoingMediaStreamStatsState) => {
            return track ? {
                codec: streamState.audioTrackStat.codec || "unknown",
                codecPayloadType: streamState.audioTrackStat.codecPayloadType || -1,
                ssrc: streamState.audioTrackStat.ssrc || -1,
                packetsSent: {
                    total: streamState.audioTrackStat.packetsSent.value,
                    derivative: streamState.audioTrackStat.packetsSent.derivative,
                },
                bytesSent: {
                    total: streamState.audioTrackStat.bytesSent.value,
                    derivative: streamState.audioTrackStat.bytesSent.derivative,
                },
                retransmittedBytesSent: {
                    total: streamState.audioTrackStat.retransmittedBytesSent.value,
                    derivative: streamState.audioTrackStat.retransmittedBytesSent.derivative,
                },
                retransmittedPacketsSent: {
                    total: streamState.audioTrackStat.retransmittedPacketsSent.value,
                    derivative: streamState.audioTrackStat.retransmittedPacketsSent.derivative,
                },
                headerBytesSent: {
                    total: streamState.audioTrackStat.headerBytesSent.value,
                    derivative: streamState.audioTrackStat.headerBytesSent.derivative,
                },
                bytesDiscardedOnSend: {
                    total: streamState.audioTrackStat.bytesDiscardedOnSend.value,
                    derivative: streamState.audioTrackStat.bytesDiscardedOnSend.derivative,
                },
                packetsDiscardedOnSend: {
                    total: streamState.audioTrackStat.packetsDiscardedOnSend.value,
                    derivative: streamState.audioTrackStat.packetsDiscardedOnSend.derivative,
                },
                nackCount: {
                    total: streamState.audioTrackStat.nackCount.value,
                    derivative: streamState.audioTrackStat.nackCount.derivative,
                },
                audioLevel: {
                    value: streamState.audioTrackStat.audioLevel.lastValue || 0,
                },
                echoReturnLoss: {
                    value: streamState.audioTrackStat.echoReturnLoss.lastValue || 0,
                },
                targetBitrate: {
                    value: streamState.audioTrackStat.targetBitrate.lastValue || 0
                },
                jitter: streamState.audioTrackStat.jitter.isEmpty ? null : {
                    value: (streamState.audioTrackStat.jitter.lastValue || 0),
                },
                rtt: (streamState.audioTrackStat.roundTripTime.isEmpty || streamState.audioTrackStat.roundTripTimeMeasurements.value === 0) ? null : {
                    current: (streamState.audioTrackStat.roundTripTime.lastValue || 0),
                    average: streamState.audioTrackStat.totalRoundTripTime.value / streamState.audioTrackStat.roundTripTimeMeasurements.value,
                },
                estimatedPacketsLoss: (streamState.audioTrackStat.packetsLost.isEmpty || streamState.audioTrackStat.fractionLost.isEmpty) ? null : {
                    fractionLost: streamState.audioTrackStat.fractionLost.lastValue || 0,
                    packetsLost: streamState.audioTrackStat.packetsLost.value || 0,
                }
            } : undefined
        }

        return {
            screen: {
                stream: this.screenAndAudio.stream,
                audioTrackStat: createAudioTrackStats(screenAudioTrack, this.screenAndAudio),
                videoTrackStat: createVideoTrackStats(screenVideoTrack, this.screenAndAudio),
            },
            microphoneAndCamera: {
                stream: this.cameraAndMicrophone.stream,
                audioTrackStat: createAudioTrackStats(micTack, this.cameraAndMicrophone),
                videoTrackStat: createVideoTrackStats(camTrack, this.cameraAndMicrophone),
            }
        }
    }

    public get viewState(): RoomViewState {
        return {
            members: this.roomMembers.viewState,
            outgoingMedia: this.outgoingMediaViewState,
            transportStatistics: this.transportStatisticsViewState
        };
    }

    public get viewStateChanged(): rx.Observable<RoomViewState> {
        return this.viewStateChangedSubj.pipe(
            rxops.map(_ => this.viewState)
        )
    }

    public async join(): Promise<void> {
        await this.queue.push(async () => {
            await this.joinRoom();
        });
    }

    public async leave() {
        this.queue.push(async () => {
            await this.leaveRoom();
        });
    }

    public async closeWebSocket(): Promise<void> {
        await this.queue.push(async () => {
            this.signaling.close()
        });
    }

    public async restartIce(): Promise<void> {
        await this.queue.push(async () => {
            this.signaling.restartIce()
        })
    }

    private async onSignalingStateChanged(state: SignalingState) {
        await this.queue.push(async () => {
            console.log("onSignalingStateChanged: ", state);
            if (!this.session) {
                console.log("The session is already terminated");
                return;
            }
            if (state === SignalingState.Interrupted) {
                const endpointAndTicket = await this.acquireTicketWithRetry(8);
                if (!endpointAndTicket) {
                    console.error("Failed to get new ticket to re-join room");
                    return;
                }

                this.signaling.connect(endpointAndTicket.getEndpoint());
                try {
                    const resp = await this.signaling.authenticate(endpointAndTicket.getTicket());
                    if ('error' in resp) {
                        console.error("New connection authentication error: ", resp);
                        this.destroyRoomSession();
                        return;
                    }
                    if (!resp.authenticate.join_required) {
                        console.log("Signaling connection successfully recovered with existing room session");
                        return;
                    }
                    console.log("Signaling connection successfully recovered, but our session on server expired. Will re-join.");
                } catch (e) {
                    console.error("Failed to re-join room");
                    this.destroyRoomSession();
                    return;
                }
            }

            switch (state) {
                case SignalingState.Interrupted: {
                    this.destroyRoomSession();
                    await this.joinRoom();
                    this.sendCameraVideo();
                    this.sendMicrophoneTrack();
                    this.updateProducedMedia();
                    break;
                }
            }
        })
    }

    private async onMaintenanceNotification(n: { maintenance: messages.MaintenanceNotification }) {
        await this.queue.push(async () => {
            console.log("onMaintenanceNotification: ", n.maintenance);

            console.log("Will destroy old session and join room again");
            this.destroyRoomSession();
            await this.joinRoom();
            this.sendCameraVideo();
            this.sendMicrophoneTrack();
            this.updateProducedMedia();
        })
    }

    public async setPausedForIncomingTracks(tracks: Array<{ mid: string, pause: boolean }>) {
        if (this.signaling) {
            try {
                await this.signaling.updateMedia({
                    consumed_tracks: tracks
                })
            } catch (e) {
                console.error("Failed to send update media request: ", e);
            }
        } 
    }

    public async updatePrefferedLayer(mid: string, preffered_layer?: messages.VideoLayer) {
        if (this.signaling) {
            try {
                await this.signaling.updateMedia({
                    consumed_tracks: [
                        {
                            mid: mid,
                            pref_layer: preffered_layer,
                        }
                    ]
                })
            } catch (e) {
                console.error("Failed to send update media request: ", e);
            }
        } 
    }

    private async onRoomUpdatedNotification(n: { room_updated: messages.RoomUpdatedNotification }) {
        await this.queue.push(async () => {
            console.log("room updated notification: ", n);
            for (let member of n.room_updated.members) {
                this.roomMembers.updateMember(member);
            }
            if (n.room_updated.consumed_media_sdp_offer) {
                if (this.session) {
                    await this.session.obj.applyConsumedSdpOffer(n.room_updated.consumed_media_sdp_offer)
                } else {
                    console.error("Received sdp while there is no active session");
                }
                const consumeTracksStates = this.roomMembers.unpauseCameraIncomingTracks();
                if (this.signaling) {
                    try {
                        await this.signaling.updateMedia({
                            consumed_tracks: consumeTracksStates
                        })
                    } catch (e) {
                        console.error("Failed to send update media request: ", e);
                    }
                }
            }
            this.viewStateChangedSubj.next();
        })
    }

    private async onTrackAdded(transceiver: RTCRtpTransceiver, track: MediaStreamTrack, stream: MediaStream) {
        this.roomMembers.addTrack(transceiver.mid!, track, stream);

        if (this.configuration.enableFrequentKeyFrameRequester) {
            this.addKeyFrameRequester(transceiver);
        }
    }

    private addKeyFrameRequester(transceiver: RTCRtpTransceiver) {
        if (transceiver.receiver.track.kind !== "video") {
            return;
        }
        if (!('transform' in transceiver.receiver)) {
            console.warn("RTCRtpReceiver has no transform");
            return
        }
        if (transceiver.receiver.transform) {
            console.warn("RTCRtpReceiver has transform already assigned");
            return;
        }
        const workerUri = new URL(
            window.location.origin + "/static/KeyFrameTransform.js"
        );
        console.log("will add transform to RTCRtpReceiver with worker = ", workerUri);
        let keyframeWorker = new Worker(workerUri);
        // @ts-ignore
        transceiver.receiver.transform = new RTCRtpScriptTransform(
            keyframeWorker, 
            { name: "keyFrameRequester" }
        );
    }

    private addKeyFrameGenerator(transceiver: RTCRtpTransceiver) {
        if (transceiver.receiver.track.kind !== "video") {
            return;
        }
        if (!('transform' in transceiver.sender)) {
            console.warn("RTCRtpSender has no transform");
            return
        }
        if (transceiver.sender.transform) {
            console.warn("RTCRtpSender has transform already assigned");
            return;
        }

        const workerUri = new URL(
            window.location.origin + "/static/KeyFrameTransform.js"
        );
        console.log("will add transform to RTCRtpSender with worker = ", workerUri);
        let keyframeWorker = new Worker(workerUri);
        // @ts-ignore
        transceiver.sender.transform = new RTCRtpScriptTransform(
            keyframeWorker, 
            { name: "keyFrameGenerator" }
        );
    }

    private async onIceRestartRequired() {
        for (let i = 0; i < 5; i++) {
            try {
                await this.signaling.restartIce();
                return
            }
            catch (e) {
                console.error("Failed to initiate ICE restart", e);
                await Delay(1000 * 1.4)
            }
        }
    }

    private async onPeerConnectionStatisticsReport(report: RTCStatsReport) {
        if (!this.session) {
            console.log("No active session ignore report");
            return;
        }

        if (this.zstdDictionary !== null) {
            const data = createProtoRTCStatsReport(report).serializeBinary();
            const compressedData = await compressData(data, this.zstdDictionary.buffer);
            const reportMessage = new CompressedRTCStatsReport({
                etag: this.zstdDictionary.etag,
                report: compressedData,
                peer_connection_id: this.session.obj.id,
            });
            this.signaling.send_report(reportMessage);
        }

        for (const rep of report.values()) {

            const stats = rep as stat.RTCStatsReport
            switch (stats.type) {
                case "transport": {
                    if (typeof stats.bytesReceived !== 'undefined') {
                        this.transportStatistics.bytesReceived.update(stats.bytesReceived, stats.timestamp);
                    }
                    if (typeof stats.packetsReceived !== 'undefined') {
                        this.transportStatistics.packetsReceived.update(stats.packetsReceived, stats.timestamp);
                    }
                    if (typeof stats.bytesSent !== 'undefined') {
                        this.transportStatistics.bytesSent.update(stats.bytesSent, stats.timestamp);
                    }
                    if (typeof stats.packetsSent !== 'undefined') {
                        this.transportStatistics.packetsSent.update(stats.packetsSent, stats.timestamp);
                    }
                    if (typeof stats.iceState !== 'undefined') {
                        this.transportStatistics.iceState = stats.iceState
                    }
                    if (typeof stats.selectedCandidatePairId !== 'undefined') {
                        const pairStats = report.get(stats.selectedCandidatePairId) as (stat.RTCStatsReport | undefined);
                        if (pairStats && pairStats.type === 'candidate-pair') {
                            if (!this.transportStatistics.candidatePair || pairStats.id !== this.transportStatistics.candidatePair.id) {
                                this.transportStatistics.candidatePair = {
                                    id: pairStats.id,
                                    bytesSent: new CounterMetric("candidatePairBytesSent", 60),
                                    bytesReceived: new CounterMetric("candidatePairBytesReceived", 60),
                                    packetsSent: new CounterMetric("candidatePairPacketsSent", 60),
                                    packetsReceived: new CounterMetric("candidatePairPacketsReceived", 60),
                                    currentRoundTripTime: new TimeSeries("candidatePairCurrentRtt", 60),
                                    availableOutgoingBitrate: new TimeSeries("candidatePairAvailableOutgoingBitrate", 60),
                                    availableIncomingBitrate: new TimeSeries("candidatePairAvailableIncomingBitrate", 60),
                                    local: {
                                    },
                                    remote: {
                                    }
                                }
                            }
                            if (typeof pairStats.bytesSent !== 'undefined') {
                                this.transportStatistics.candidatePair.bytesSent.update(pairStats.bytesSent, pairStats.timestamp);
                            }
                            if (typeof pairStats.bytesReceived !== 'undefined') {
                                this.transportStatistics.candidatePair.bytesReceived.update(pairStats.bytesReceived, pairStats.timestamp);
                            }
                            if (typeof pairStats.packetsSent !== 'undefined') {
                                this.transportStatistics.candidatePair.packetsSent.update(pairStats.packetsSent, pairStats.timestamp);
                            }
                            if (typeof pairStats.packetsReceived !== 'undefined') {
                                this.transportStatistics.candidatePair.packetsReceived.update(pairStats.packetsReceived, pairStats.timestamp);
                            }
                            if (typeof pairStats.currentRoundTripTime !== 'undefined') {
                                this.transportStatistics.candidatePair.currentRoundTripTime.addValue(pairStats.currentRoundTripTime, pairStats.timestamp);
                            }
                            if (typeof pairStats.availableIncomingBitrate !== 'undefined') {
                                this.transportStatistics.candidatePair.availableIncomingBitrate.addValue(pairStats.availableIncomingBitrate, pairStats.timestamp);
                            }
                            if (typeof pairStats.availableOutgoingBitrate !== 'undefined') {
                                this.transportStatistics.candidatePair.availableOutgoingBitrate.addValue(pairStats.availableOutgoingBitrate, pairStats.timestamp);
                            }

                            const localCandidate = report.get(pairStats.localCandidateId) as (stat.RTCStatsReport | undefined);
                            if (localCandidate && localCandidate.type === 'local-candidate') {
                                if (typeof localCandidate.address !== 'undefined') {
                                    this.transportStatistics.candidatePair.local.address = localCandidate.address;
                                }
                                if (typeof localCandidate.protocol !== 'undefined') {
                                    this.transportStatistics.candidatePair.local.protocol = localCandidate.protocol;
                                }
                                if (typeof localCandidate.port !== 'undefined') {
                                    this.transportStatistics.candidatePair.local.port = localCandidate.port;
                                }
                                if (typeof localCandidate.candidateType !== 'undefined') {
                                    this.transportStatistics.candidatePair.local.candidateType = localCandidate.candidateType;
                                }
                                if (typeof localCandidate.relayProtocol !== 'undefined') {
                                    this.transportStatistics.candidatePair.local.relayProtocol = localCandidate.relayProtocol;
                                }
                            }
                            const remoteCandidate = report.get(pairStats.remoteCandidateId) as (stat.RTCStatsReport | undefined);
                            if (remoteCandidate && remoteCandidate.type === 'remote-candidate') {
                                if (typeof remoteCandidate.address !== 'undefined') {
                                    this.transportStatistics.candidatePair.remote.address = remoteCandidate.address;
                                }
                                if (typeof remoteCandidate.protocol !== 'undefined') {
                                    this.transportStatistics.candidatePair.remote.protocol = remoteCandidate.protocol;
                                }
                                if (typeof remoteCandidate.port !== 'undefined') {
                                    this.transportStatistics.candidatePair.remote.port = remoteCandidate.port;
                                }
                            }
                        }

                    }
                    break;
                }
                case "media-source": {
                    break;
                }
                case "sender": {
                    console.log("Sender report", report);
                    break;
                }
                case "receiver": {
                    console.log("Receiver report", report);
                    break;
                }
                case "outbound-rtp": {
                    if ('mediaSourceId' in stats && stats.mediaSourceId) {
                        const mediaSource = report.get(stats.mediaSourceId) as stat.RTCStatsReport | undefined;
                        if (!mediaSource || mediaSource.type !== 'media-source') {
                            console.log("Unable to find media-source stat by id:", stats.mediaSourceId)
                            break;
                        }
                        if (mediaSource.kind === 'audio' && stats.kind === 'audio') {
                            for (const outgoingStream of [this.cameraAndMicrophone, this.screenAndAudio]) {
                                if (!outgoingStream.stream.getTrackById(mediaSource.trackIdentifier)){
                                    continue;
                                }
                                if (typeof mediaSource.audioLevel !== 'undefined') {
                                    outgoingStream.audioTrackStat.audioLevel.addValue(mediaSource.audioLevel, stats.timestamp)
                                }
                                if (typeof mediaSource.echoReturnLoss !== 'undefined') {
                                    outgoingStream.audioTrackStat.echoReturnLoss.addValue(mediaSource.echoReturnLoss, stats.timestamp)
                                }
                                if (typeof stats.codecId !== 'undefined') {
                                    const codec = report.get(stats.codecId) as stat.RTCStatsReport | undefined;
                                    if (codec && codec.type === 'codec') {
                                        outgoingStream.audioTrackStat.codec = codec.mimeType;
                                        outgoingStream.audioTrackStat.codecPayloadType = codec.payloadType;
                                    }
                                }
                                if (typeof stats.remoteId !== 'undefined') {
                                    const rtcpStat = report.get(stats.remoteId) as stat.RTCStatsReport | undefined;
                                    if (rtcpStat && rtcpStat.type === 'remote-inbound-rtp') {
                                        if (typeof rtcpStat.jitter !== 'undefined') {
                                            outgoingStream.audioTrackStat.jitter.addValue(rtcpStat.jitter, rtcpStat.timestamp)
                                        }
                                        if (typeof rtcpStat.packetsLost !== 'undefined') {
                                            outgoingStream.audioTrackStat.packetsLost.update(rtcpStat.packetsLost, rtcpStat.timestamp)
                                        }
                                        if (typeof rtcpStat.fractionLost !== 'undefined') {
                                            outgoingStream.audioTrackStat.fractionLost.addValue(rtcpStat.fractionLost, rtcpStat.timestamp)
                                        }
                                        if (typeof rtcpStat.roundTripTime !== 'undefined') {
                                            outgoingStream.audioTrackStat.roundTripTime.addValue(rtcpStat.roundTripTime, rtcpStat.timestamp)
                                        }
                                        if (typeof rtcpStat.roundTripTimeMeasurements !== 'undefined') {
                                            outgoingStream.audioTrackStat.roundTripTimeMeasurements.update(rtcpStat.roundTripTimeMeasurements, rtcpStat.timestamp)
                                        }
                                        if (typeof rtcpStat.totalRoundTripTime !== 'undefined') {
                                            outgoingStream.audioTrackStat.totalRoundTripTime.update(rtcpStat.totalRoundTripTime, rtcpStat.timestamp)
                                        }
                                    }
                                }

                                outgoingStream.audioTrackStat.ssrc = stats.ssrc;
                                if (typeof stats.bytesSent !== 'undefined') {
                                    outgoingStream.audioTrackStat.bytesSent.update(stats.bytesSent, stats.timestamp);
                                } 
                                if (typeof stats.headerBytesSent !== 'undefined') {
                                    outgoingStream.audioTrackStat.headerBytesSent.update(stats.headerBytesSent, stats.timestamp);
                                }
                                if (typeof stats.packetsSent !== 'undefined') {
                                    outgoingStream.audioTrackStat.packetsSent.update(stats.packetsSent, stats.timestamp);
                                }
                                break;
                            }
                        } else if (mediaSource.kind === 'video' && stats.kind === 'video') {
                            for (const outgoingStream of [this.cameraAndMicrophone, this.screenAndAudio]) {
                                if (!outgoingStream.stream.getTrackById(mediaSource.trackIdentifier)){
                                    continue;
                                }
                                if (typeof mediaSource.width !== 'undefined') {
                                    outgoingStream.videoTrackStat.captureWidth = mediaSource.width;
                                }
                                if (typeof mediaSource.height !== 'undefined') {
                                    outgoingStream.videoTrackStat.captureHeight = mediaSource.height;
                                }
                                if (typeof mediaSource.framesPerSecond !== 'undefined') {
                                    outgoingStream.videoTrackStat.captureFps = mediaSource.framesPerSecond;
                                }
                                let layer_stat: VideoLayerStat;
                                if (!outgoingStream.videoTrackStat.layers.has(stats.ssrc)) {
                                    layer_stat = {
                                        ssrc: stats.ssrc,
                                        frameHeight: new TimeSeries("frameHeight", 60),
                                        frameWidth: new TimeSeries("frameWidth", 60),
                                        bytesSent: new CounterMetric("bytesSent", 60),
                                        packetsSent: new CounterMetric("packetsSent", 60),
                                        bytesDiscardedOnSend: new CounterMetric("bytesDiscardedOnSend", 60),
                                        framesDiscardedOnSend: new CounterMetric("framesDiscardedOnSend", 60),
                                        packetsDiscardedOnSend: new CounterMetric("packetsDiscardedOnSend", 60),
                                        firCount: new CounterMetric("firCount", 60),
                                        pliCount: new CounterMetric("pliCount", 60),
                                        nackCount: new CounterMetric("nackCount", 60),
                                        framesEncoded: new CounterMetric("framesEncoded", 60),
                                        keyFramesEncoded: new CounterMetric("keyFramesEncoded", 60),
                                        framesSent: new CounterMetric("framesSent", 60),
                                        headerBytesSent: new CounterMetric("headerBytesSent", 60),
                                        hugeFramesSent: new CounterMetric("hugeFramesSent", 60),
                                        retransmittedBytesSent: new CounterMetric("retransmittedBytesSent", 60),
                                        retransmittedPacketsSent: new CounterMetric("retransmittedPacketsSent", 60),
                                        encodedFramesPerSecond: new TimeSeries("encodedFramesPerSecond", 60),
                                        targetBitrate: new TimeSeries("targetBitrate", 60),
                                        qualityLimitationReason: "none",
                                        qualityLimitationDuration: {
                                            bandwidth: new CounterMetric("qualityLimitationDurationBandwidth", 60),
                                            cpu: new CounterMetric("qualityLimitationDurationCpu", 60),
                                            none: new CounterMetric("qualityLimitationDurationNone", 60),
                                            other: new CounterMetric("qualityLimitationDurationOther", 60),
                                        },
                                        totalEncodeTime: new CounterMetric("totalEncodeTime", 60),
                                        totalPacketSendDelay: new CounterMetric("totalPacketSendDelay", 60),
                                        jitter: new TimeSeries("jitter", 60),
                                        packetsLost: new CounterMetric("packetsLost", 60),
                                        fractionLost: new TimeSeries("fractionLost", 60),
                                        roundTripTime: new TimeSeries("roundTripTime", 60),
                                        roundTripTimeMeasurements: new CounterMetric("roundTripTimeMeasurements", 60),
                                        totalRoundTripTime: new CounterMetric("totalRoundTripTime", 60),
                                    };
                                    outgoingStream.videoTrackStat.layers.set(stats.ssrc, layer_stat);
                                } else {
                                    layer_stat = outgoingStream.videoTrackStat.layers.get(stats.ssrc) as VideoLayerStat;
                                }
                                if (stats.codecId) {
                                    const codec = report.get(stats.codecId) as stat.RTCStatsReport | undefined;
                                    if (codec && codec.type === 'codec') {
                                        layer_stat.codec = codec.mimeType;
                                        layer_stat.codecPayloadType = codec.payloadType;
                                    }
                                }
                                if (stats.remoteId) {
                                    const rtcpStat = report.get(stats.remoteId) as stat.RTCStatsReport | undefined;
                                    if (rtcpStat && rtcpStat.type === 'remote-inbound-rtp') {
                                        if (typeof rtcpStat.jitter !== 'undefined') {
                                            layer_stat.jitter.addValue(rtcpStat.jitter, rtcpStat.timestamp)
                                        }
                                        if (typeof rtcpStat.packetsLost !== 'undefined') {
                                            layer_stat.packetsLost.update(rtcpStat.packetsLost, rtcpStat.timestamp)
                                        }
                                        if (typeof rtcpStat.fractionLost !== 'undefined') {
                                            layer_stat.fractionLost.addValue(rtcpStat.fractionLost, rtcpStat.timestamp)
                                        }
                                        if (typeof rtcpStat.roundTripTime !== 'undefined') {
                                            layer_stat.roundTripTime.addValue(rtcpStat.roundTripTime, rtcpStat.timestamp)
                                        }
                                        if (typeof rtcpStat.roundTripTimeMeasurements !== 'undefined') {
                                            layer_stat.roundTripTimeMeasurements.update(rtcpStat.roundTripTimeMeasurements, rtcpStat.timestamp)
                                        }
                                        if (typeof rtcpStat.totalRoundTripTime !== 'undefined') {
                                            layer_stat.totalRoundTripTime.update(rtcpStat.totalRoundTripTime, rtcpStat.timestamp)
                                        }
                                    }
                                }

                                layer_stat.ssrc = stats.ssrc;
                                layer_stat.rid = stats.rid;
                                layer_stat.rtxSsrc = stats.rtxSsrc;
                                if (typeof stats.frameWidth !== 'undefined') {
                                    layer_stat.frameWidth.addValue(stats.frameWidth, stats.timestamp);
                                }
                                if (typeof stats.frameHeight !== 'undefined') {
                                    layer_stat.frameHeight.addValue(stats.frameHeight, stats.timestamp);
                                }
                                if (typeof stats.bytesSent !== 'undefined') {
                                    layer_stat.bytesSent.update(stats.bytesSent, stats.timestamp);
                                }
                                if (typeof stats.packetsSent !== 'undefined') {
                                    layer_stat.packetsSent.update(stats.packetsSent, stats.timestamp);
                                }
                                if (typeof stats.bytesDiscardedOnSend !== 'undefined') {
                                    layer_stat.bytesDiscardedOnSend.update(stats.bytesDiscardedOnSend, stats.timestamp);
                                }
                                if (typeof stats.framesDiscardedOnSend !== 'undefined') {
                                    layer_stat.framesDiscardedOnSend.update(stats.framesDiscardedOnSend, stats.timestamp);
                                }
                                if (typeof stats.packetsDiscardedOnSend !== 'undefined') {
                                    layer_stat.packetsDiscardedOnSend.update(stats.packetsDiscardedOnSend, stats.timestamp);
                                }
                                if (typeof stats.firCount !== 'undefined') {
                                    layer_stat.firCount.update(stats.firCount, stats.timestamp);
                                }
                                if (typeof stats.pliCount !== 'undefined') {
                                    layer_stat.pliCount.update(stats.pliCount, stats.timestamp);
                                }
                                if (typeof stats.nackCount !== 'undefined') {
                                    layer_stat.nackCount.update(stats.nackCount, stats.timestamp);
                                }
                                if (typeof stats.framesEncoded !== 'undefined') {
                                    layer_stat.framesEncoded.update(stats.framesEncoded, stats.timestamp);
                                }
                                if (typeof stats.keyFramesEncoded !== 'undefined') {
                                    layer_stat.keyFramesEncoded.update(stats.keyFramesEncoded, stats.timestamp);
                                }
                                if (typeof stats.framesSent !== 'undefined') {
                                    layer_stat.framesSent.update(stats.framesSent, stats.timestamp);
                                }
                                if (typeof stats.headerBytesSent !== 'undefined') {
                                    layer_stat.headerBytesSent.update(stats.headerBytesSent, stats.timestamp);
                                }
                                if (typeof stats.hugeFramesSent !== 'undefined') {
                                    layer_stat.hugeFramesSent.update(stats.hugeFramesSent, stats.timestamp);
                                }
                                if (typeof stats.retransmittedBytesSent !== 'undefined') {
                                    layer_stat.retransmittedBytesSent.update(stats.retransmittedBytesSent, stats.timestamp);
                                }
                                if (typeof stats.retransmittedPacketsSent !== 'undefined') {
                                    layer_stat.retransmittedPacketsSent.update(stats.retransmittedPacketsSent, stats.timestamp);
                                }
                                if (typeof stats.framesPerSecond !== 'undefined') {
                                    layer_stat.encodedFramesPerSecond.addValue(stats.framesPerSecond, stats.timestamp);
                                }
                                if (typeof stats.targetBitrate !== 'undefined') {
                                    layer_stat.targetBitrate.addValue(stats.targetBitrate, stats.timestamp);
                                }
                                if (typeof stats.qualityLimitationReason !== 'undefined') {
                                    layer_stat.qualityLimitationReason = stats.qualityLimitationReason;
                                }
                                if (typeof stats.qualityLimitationDurations !== 'undefined') {
                                    layer_stat.qualityLimitationDuration.bandwidth.update(stats.qualityLimitationDurations['bandwidth'], stats.timestamp);
                                    layer_stat.qualityLimitationDuration.cpu.update(stats.qualityLimitationDurations['cpu'], stats.timestamp);
                                    layer_stat.qualityLimitationDuration.none.update(stats.qualityLimitationDurations['none'], stats.timestamp);
                                    layer_stat.qualityLimitationDuration.other.update(stats.qualityLimitationDurations['other'], stats.timestamp);
                                }
                                if (typeof stats.totalEncodeTime !== 'undefined') {
                                    layer_stat.totalEncodeTime.update(stats.totalEncodeTime, stats.timestamp);
                                }
                                if (typeof stats.totalPacketSendDelay !== 'undefined') {
                                    layer_stat.totalPacketSendDelay.update(stats.totalPacketSendDelay, stats.timestamp);
                                }
                                break;
                            }

                        } else {
                            console.error("outbound-rtp and media-source reports has different kind's: ", stats, mediaSource);
                        }
                    } else {
                        if ((stats.kind === 'audio' && (this.session.obj.microphoneSendTransceiver.currentDirection !== "inactive" || this.session.obj.screenAudioSendTransceiver?.currentDirection !== "inactive")) ||
                            (stats.kind === 'video' && ((this.session.obj.cameraSendTransceiver && this.session.obj.cameraSendTransceiver?.currentDirection !== "inactive") || (this.session.obj.screenSendTransceiver && this.session.obj.screenSendTransceiver?.currentDirection !== "inactive")))) {
                            // console.warn("Unable to correlate outbound-rtp to media source", stats);
                        }
                    }
                    break;
                }
                case "remote-inbound-rtp": {
                    break;
                }
                case "inbound-rtp": {
                    if (stats.kind === 'video') {
                        //report.frameBitDepth
                    }
                    break;
                }
                case "remote-outbound-rtp": {
                    break;
                }
                case "codec": {
                    break;
                }
                case "csrc": {
                    break;
                }
                case "peer-connection": {
                    break;
                }
            }
        }
        this.viewStateChangedSubj.next()
    }

    public async startMicrophoneTrack() {
        await this.queue.push(async () => {
            console.log("startMicrophoneTrack");
            if (this.microphoneTrack != null) {
                this.microphoneTrack.stop();
                this.microphoneTrack = null;
            }

            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    audio: {
                        autoGainControl: true,
                        echoCancellation: true,
                        noiseSuppression: true,
                        sampleRate: 48000,
                        latency: 0.01,
                        channel: 1,
                    }
                } as any);

                const audioTrack = stream.getAudioTracks()[0];

                this.microphoneTrack = audioTrack;

                console.log("microphone track settings: ", this.microphoneTrack.getSettings())
            } catch (error) {
                console.log(`Failed to build audio track error is ${error}`);
            }
        })
    }

    public async stopMicrophoneTrack() {
        await this.queue.push(async () => {
            console.log("stopMicrophoneTrack")
            if (this.microphoneTrack) {
                this.microphoneTrack.stop();
                this.microphoneTrack = null;
            }

            if (this.session) {
                await this.session.obj.microphoneSendTransceiver.sender.replaceTrack(null);
            }
        })
    }

    public async sendMicrophoneTrack() {
        await this.queue.push(async () => {
            console.log("sendMicrophoneTrack");
            if (!this.microphoneTrack) {
                console.warn("No microphone track to send");
                return;
            }
            if (!this.session) {
                console.warn("No active session");
                return;
            }
            await this.session.obj.microphoneSendTransceiver.sender.replaceTrack(this.microphoneTrack);
        })
    }

    public async startCameraTrack(facingMode: "user" | "environment") {
        await this.queue.push(async () => {
            console.log("startCameraTrack");
            if (this.cameraTrack) {
                this.cameraTrack.stop();
                this.cameraTrack = null;
            }

            let constraints = {
                video: {
                    facingMode: { ideal: facingMode },
                    aspectRatio: { ideal: 16.0 / 9.0 }
                }
            }
            if (this.configuration.maxCameraCaptureDimension === null) {
                Object.assign(constraints.video, { 
                    height: { ideal: facingMode === "user" ? 1080 : 2160 },
                    width: { ideal: facingMode === "user" ? 1920 : 3840 },
                })
            } else {
                Object.assign(constraints.video, { 
                    height: { 
                        max: this.configuration.maxCameraCaptureDimension,
                        ideal: this.configuration.maxCameraCaptureDimension 
                    },
                    width: { 
                        max: this.configuration.maxCameraCaptureDimension,
                        ideal: this.configuration.maxCameraCaptureDimension 
                    },
                })
            }
            try {
                const stream = await navigator.mediaDevices.getUserMedia(constraints);

                const cameraTrack = stream.getVideoTracks()[0];
                console.log("Camera track settings: ", cameraTrack.getSettings());
                console.log("Camera track constraints: ", cameraTrack.getConstraints());

                this.cameraTrack = cameraTrack;
            } catch (error) {
                console.log(`Failed to build video track, error is ${error}`);
            }
        });
    }

    public async startScreenTracks() {
        if (this.configuration.enableScreenShare === false) {
            return;
        }
        await this.queue.push(async () => {
            console.log("startScreenTracks");
            if (this.screenVideoTrack) {
                this.screenVideoTrack.stop();
                this.screenVideoTrack = null;
            }

            if (this.screenAudioTrack) {
                this.screenAudioTrack.stop();
                this.screenAudioTrack = null;
            }

            try {
                const stream = await navigator.mediaDevices.getDisplayMedia({
                    audio: true,
                    video: {
                        height: { ideal: 1080 },
                        width: { ideal: 1920 },
                        aspectRatio: { ideal: 16.0 / 9.0 }
                    }
                });

                const screenVideoTrack = stream.getVideoTracks()[0];
                screenVideoTrack.contentHint = "text";
                console.log("Screen video track settings: ", screenVideoTrack.getSettings());
                console.log("Screen video track constraints: ", screenVideoTrack.getConstraints());
                this.screenVideoTrack = screenVideoTrack;

                if (stream.getAudioTracks().length > 0) {
                    const screenAudioTrack = stream.getAudioTracks()[0];
                    console.log("Screen audio track settings: ", screenAudioTrack.getSettings());
                    console.log("Screen audio track constraints: ", screenAudioTrack.getConstraints());
                    this.screenAudioTrack = screenAudioTrack;
                }
            } catch (error) {
                console.log(`Failed to build video track, error is ${error}`);
            }
        });
    }

    public async stopScreenTracks() {
        await this.queue.push(async () => {
            console.log("stopScreenTracks");
            if (this.screenVideoTrack) {
                this.screenVideoTrack.stop();
                this.screenVideoTrack = null;
            }

            if (this.screenAudioTrack) {
                this.screenAudioTrack.stop();
                this.screenAudioTrack = null;
            }

            if (this.session) {
                const screenSendTransceiver = this.session.obj.screenSendTransceiver;
                if (screenSendTransceiver) {
                    await screenSendTransceiver.sender.replaceTrack(null);
                }

                const screenAudioSendTransceiver = this.session.obj.screenAudioSendTransceiver;
                if (screenAudioSendTransceiver) {
                    await screenAudioSendTransceiver.sender.replaceTrack(null);
                }
            }
        })
    }

    public async stopCameraTrack() {
        await this.queue.push(async () => {
            console.log("stopCameraTrack");
            if (this.cameraTrack) {
                this.cameraTrack.stop();
                this.cameraTrack = null;
            }

            if (this.session) {
                const cameraSendTransceiver = this.session.obj.cameraSendTransceiver;
                if (cameraSendTransceiver) {
                    await cameraSendTransceiver.sender.replaceTrack(null);
                }
            }
        })
    }

    public async sendScreenVideoAndAudio() {
        await this.queue.push(async () => {
            console.log("sendScreenVideoAndAudio");
            if (!this.screenVideoTrack) {
                console.warn("No screen track to send");
                return;
            }
            if (!this.session) {
                console.warn("No active session");
                return;
            }
            const screenSendTransceiver = this.session.obj.screenSendTransceiver;
            if (screenSendTransceiver) {
                await screenSendTransceiver.sender.replaceTrack(this.screenVideoTrack);
            }

            const screenAudioSendTransceiver = this.session.obj.screenAudioSendTransceiver;
            if (screenAudioSendTransceiver && this.screenAudioTrack) {
                await screenAudioSendTransceiver.sender.replaceTrack(this.screenAudioTrack);
                if (this.configuration.enableFrequentKeyFrameGenerator) {
                    this.addKeyFrameGenerator(screenAudioSendTransceiver);
                }
            }
        })
    }

    public async sendCameraVideo() {
        await this.queue.push(async () => {
            console.log("sendCameraVideo");
            if (!this.cameraTrack) {
                console.warn("No camera track to send");
                return;
            }
            if (!this.session) {
                console.warn("No active session");
                return;
            }
            const cameraSendTransceiver = this.session.obj.cameraSendTransceiver;
            if (cameraSendTransceiver) {
                await cameraSendTransceiver.sender.replaceTrack(this.cameraTrack);
                if (this.configuration.enableFrequentKeyFrameGenerator) {
                    this.addKeyFrameGenerator(cameraSendTransceiver);
                }
            }
        })
    }

    public async updateProducedMedia() {
        await this.queue.push(async () => {
            const session = this.session?.obj;
            if (!session) {
                console.log("No active session");
                return;
            }

            const cameraTrack = session.cameraSendTransceiver?.sender.track || null;
            const microphoneTrack = session.microphoneSendTransceiver.sender.track;
            const screenVideoTrack = session.screenSendTransceiver?.sender.track || null;
            const screenAudioTrack = session.screenAudioSendTransceiver?.sender.track || null;
            const producedTracks = []

            if (session.microphoneSendTransceiver.mid && session.microphoneSendTransceiver.currentDirection !== "inactive") {
                producedTracks.push({
                    mid: session.microphoneSendTransceiver.mid,
                    src: { aud: messages.AudioSource.Microphone },
                    st: microphoneTrack === null
                        ? messages.TrackStatus.Off
                        : (microphoneTrack.muted || !microphoneTrack.enabled
                            ? messages.TrackStatus.Muted
                            : messages.TrackStatus.On)
                })
            }
            if (session.cameraSendTransceiver && session.cameraSendTransceiver.mid && session.cameraSendTransceiver.currentDirection !== "inactive") {
                producedTracks.push({
                    mid: session.cameraSendTransceiver.mid,
                    src: { vid: messages.VideoSource.Camera },
                    st: cameraTrack === null
                        ? messages.TrackStatus.Off
                        : (cameraTrack.muted || !cameraTrack.enabled
                            ? messages.TrackStatus.Muted
                            : messages.TrackStatus.On)
                })
            }

            if (session.screenSendTransceiver && session.screenSendTransceiver.mid && session.screenSendTransceiver.currentDirection !== "inactive") {
                producedTracks.push({
                    mid: session.screenSendTransceiver.mid,
                    src: { vid: messages.VideoSource.Screen },
                    st: screenVideoTrack === null
                        ? messages.TrackStatus.Off
                        : (screenVideoTrack.muted || !screenVideoTrack.enabled
                            ? messages.TrackStatus.Muted
                            : messages.TrackStatus.On)
                })
            }

            if (session.screenAudioSendTransceiver && session.screenAudioSendTransceiver.mid && session.screenAudioSendTransceiver.currentDirection !== "inactive") {
                producedTracks.push({
                    mid: session.screenAudioSendTransceiver.mid,
                    src: { aud: messages.AudioSource.Screen },
                    st: screenAudioTrack === null
                        ? messages.TrackStatus.Off
                        : (screenAudioTrack.muted || !screenAudioTrack.enabled
                            ? messages.TrackStatus.Muted
                            : messages.TrackStatus.On)
                })
            }

            try {
                if (producedTracks.length > 0) {
                    await this.signaling.updateMedia({
                        produced_tracks: producedTracks
                    });
                }
            }
            catch (e) {
                console.log("Failed to send update media request: ", e);
            }
        })
    }

    private async joinRoom() {
        console.log(`[room = ${this.configuration.roomId}]: join room`)
        

        const region = this.configuration.region || await this.detectRegion();

        try {

            let iceServers : Array<RTCIceServerWithHostname> = [];
            let ticket = null;
            if (this.signaling.signalingState !== SignalingState.Connected) {
                const ticketAndEndpoint = await this.acquireTicketWithRetry(3);
                if (!ticketAndEndpoint) {
                    console.error("Unable to obtain ticket");
                    return;
                }
                iceServers = ticketAndEndpoint.getIceServersList()
                    .flatMap(x => x.getIceServersList())
                    .map(x => {
                        return {
                            credential: x.getCredential(),
                            urls: x.getUrlsList(),
                            username: x.getUsername(),
                            hostname: x.getHostname(),
                        };
                    });
                await this.signaling.connect(ticketAndEndpoint.getEndpoint());
                ticket = ticketAndEndpoint.getTicket();
            } else {
                console.log("Signaling is already authenticated")
            }

            this.newRoomSession(iceServers);
            if (!this.session) {
                console.error("Failed to create room session");
                return;
            }

            const initialOffer = await this.session.obj.createInitialOffer()

            const joinResponse = await this.signaling.join({
                ticket: ticket ?? undefined,
                region: region ?? undefined,
                offer: initialOffer,
                enableTcp: this.configuration.enableTcp,
                allowAutomaticSimulcast: this.configuration.allowAutomaticSimulcast ?? undefined,
                videoProducerBandwidthLimitKbps: this.configuration.videoProducerBandwidthLimitKbps ?? undefined,
            });
            if ('error' in joinResponse) {
                console.error("Failed to join room: ", joinResponse.error);
                this.destroyRoomSession();
                return;
            }

            await this.session.obj.applyProducedSdpOffer(joinResponse.join_room_v2.sdp_offer);
            await this.session.obj.applyProducedSdpAnswer(joinResponse.join_room_v2.sdp_answer);
            if (joinResponse.join_room_v2.sdp_server_offer) {
                await this.session.obj.applyConsumedSdpOffer(joinResponse.join_room_v2.sdp_server_offer);
            }
            this.updateProducedMedia()

            console.log(`Member ${this.configuration.accountId} joined the room ${this.configuration.roomId}`);
        } catch (e) {
            console.error(`Member ${this.configuration.accountId} failed to join the room ${this.configuration.roomId}`, e);
            this.destroyRoomSession();
        }
    }

    private async leaveRoom() {
        console.log(`[room = ${this.configuration.roomId}]: leave room`);
        try {
            await this.signaling.leave();
        } catch (e) {
            console.error("Failed to send Leave request: ", e);
        }
        this.signaling.close();
        this.destroyRoomSession();
        this.stopCameraTrack();
        this.stopMicrophoneTrack();
    }

    private async acquireTicket(): Promise<null | Ticket> {
        const client = new KaldunTicketsClient(this.configuration.garconUrl);
        const request = new TicketRequest();
        request.setAccountId(encodeAccountAndDisplayName(this.configuration.accountId, this.configuration.displayName));
        request.setClientId(this.configuration.clientId);
        request.setRoomId(this.configuration.roomId);

        if (this.configuration.clientRegion || this.configuration.clientRegionSubdivision) {
            const locationData = new LocationData();
            if (this.configuration.clientRegion) {
                locationData.setRegion(this.configuration.clientRegion)
            }
            if (this.configuration.clientRegionSubdivision) {
                locationData.setRegionSubdivision(this.configuration.clientRegionSubdivision)
            }
            request.setClientLocationData(locationData);
        }

        async function makeRequest(subject: KaldunTicketSubject, request: TicketRequest, client: KaldunTicketsClient): Promise<TicketResponse | null> {
            switch(subject) {
                case KaldunTicketSubject.Broadcast:
                    return await client.getBroadcastTicket(request, {});
                case KaldunTicketSubject.View:
                    return await client.getViewTicket(request, {});
                case KaldunTicketSubject.BroadcastAndView:
                    return await client.getBroadcastAndViewTicket(request, {});
                case KaldunTicketSubject.Talk:
                    return await client.getTalkTicket(request, {});
                case KaldunTicketSubject.Listen:
                    return await client.getListenTicket(request, {});
                case KaldunTicketSubject.TalkAndListen:
                    return await client.getTalkAndListenTicket(request, {});
                case KaldunTicketSubject.AudioRoomListen:
                    return await client.getAudioRoomListenTicket(request, {});
                case KaldunTicketSubject.AudioRoomTalk:
                    return await client.getAudioRoomTalkTicket(request, {});
                case KaldunTicketSubject.DatingBroadcast:
                    return await client.getDatingBroadcastTicket(request, {});
                case KaldunTicketSubject.DatingView:
                    return await client.getDatingViewTicket(request, {});
                default:
                    console.error(`unknown subject: ${subject}`);
                    return null;
            }
        }

        const ticketResponse = await makeRequest(this.configuration.subject, request, client);

        if (!ticketResponse) {
            console.error("Failed to obtain ticket");
            return null;
        }

        return ticketResponse.getTicket() ?? null;
    }

    private async acquireTicketWithRetry(trials: number): Promise<null | Ticket> {
        for (let i = 0; i < trials; i++) {
            const ticket = this.acquireTicket();
            if (ticket !== null) {
                return ticket;
            }
            await Delay(i * 300);
        }
        console.log(`Failed to obtain ticket with ${trials} trials`);
        return null;
    }

    private async detectRegion(): Promise<null | string> {
        try {
            const regionResponse = await fetch("https://regiondetector.tango.me/region", {
                method: 'GET',
                cache: 'no-cache',
                keepalive: false,
                referrerPolicy: 'no-referrer',
                mode: 'cors'
            });
            if (!regionResponse.ok) {
                console.error(`Failed to obtain response: status = ${regionResponse.status}, text = ${regionResponse.statusText}`);
                return null;
            }
            return await regionResponse.text();
        } catch (e) {
            console.error("Error during region detection: {}", e);
            return null;
        }
    }

    private destroyRoomSession() {
        if (this.session) {
            console.log(`[room = ${this.configuration.roomId}]: destroying room session`);
            this.roomMembers.clear();
            this.session.iceRestartSub.unsubscribe();
            this.session.trackAddedSub.unsubscribe();
            this.session.statisticsSub.unsubscribe();
            this.session.enableRtcReportingSub.unsubscribe();
            this.session.obj.terminate();
            this.session = null;
        }
    }

    private async enableRtcReporting(notification: EnableRTCStatsReporting) {
        if (this.zstdDictionary?.etag !== notification.etag) {
            this.zstdDictionary = await getDictionary(notification.dictionary_download_url);
        }
        console.log("enableRtcReporting: did receive ", notification)
    }

    private newRoomSession(iceServers: Array<RTCIceServerWithHostname>) {
        if (this.session) {
            throw new Error("Attempt to start new session while old one exist");
        }

        const session = new RoomSession({
            roomId: this.configuration.roomId,
            iceServers: iceServers,
            icePolicy: this.configuration.icePolicy,
            ticketSubject: this.configuration.subject,
            enableScreenShare: this.configuration.enableScreenShare ?? true,
            maxCameraSendFrameRate: this.configuration.maxCameraSendFrameRate,
            maxCameraSendBitrate: this.configuration.maxCameraSendBitrate,
        });
        const trackAddedSub = session.trackAdded.subscribe(ev => this.onTrackAdded(ev.transceiver, ev.track, ev.stream))
        const iceRestartSub = session.iceRestartRequired.subscribe(_ => this.onIceRestartRequired());
        const statisticsSub = rx.interval(1000).pipe(
            rxops.switchMap(_ => rx.from(session.getStats())),
        ).subscribe(report => this.onPeerConnectionStatisticsReport(report));
        const enableRtcReportingSub = this.signaling.enableRtcReporting.subscribe(ev => this.enableRtcReporting(ev.notification));

        this.session = {
            obj: session,
            trackAddedSub,
            iceRestartSub,
            statisticsSub,
            enableRtcReportingSub,
        }
    }
}